import ESimIcon from '@/components/icons/esim';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ReplaceHtmlWithReact } from '@/lib/replace-html-with-react';
import type { MobilePlanCardDataResponse } from '@/services/apis/getPlanCard/mobile/mobile.schema';
import { useMutation } from '@tanstack/react-query';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import {
  createBroadbandOfferPayload,
  createMobileOfferPayload,
} from '@/utils/quote-summary';
import { getQuoteSummary } from '@/services/apis/getQuoteSummary';
import { useGlobalStore } from '@/stores/store';
import { useShallow } from 'zustand/react/shallow';
import { gtmMobilePlanCardSelect, gtmSelectItem } from '@/utils/events';
import { BUNDLE, CURRENCY, MOBILE, NONE } from '@/constants/app.constants';
import { getGlobalStore } from '@/utils/store';
import CheckCircle from '@/components/icons/check-circle';

interface PlanCardMobileProps {
  mobilePlanData: MobilePlanCardDataResponse;
}

const PlanCardMobile = ({ mobilePlanData }: PlanCardMobileProps) => {
  const { setLatestMobileOffer, setIsShoppingCartOpen } = getGlobalStore();

  const {
    latestBroadbandOffer,
    latestNewDevelopmentCharge,
    latestMobileOffer,
  } = useGlobalStore(
    useShallow((store) => ({
      latestBroadbandOffer: store.shoppingCartStore.latestBroadbandOffer,
      latestMobileOffer: store.shoppingCartStore.latestMobileOffer,
      latestNewDevelopmentCharge:
        store.shoppingCartStore.latestNewDevelopmentCharge,
    }))
  );

  // NOTE: API returns an array but we only need to pick the first one.
  const planCardPrice = mobilePlanData.prices[0].amount;
  /* FIXME: Remove unnecessary optional chaining. Content and heading are required but for now optional. */
  const mobilePlanHeading = mobilePlanData.content?.heading ?? '';
  const mobilePlanSubtitle = mobilePlanData.content?.subtitle ?? '';
  const { mutate, isPending: isMutating } = useMutation({
    mutationFn: getQuoteSummary,
    mutationKey: [...apiMutationKeys.getQuoteSummary],
  });
  const handleAddToCart = () => {
    //Note: We need to attach the broadband offer payload if it exists
    const broadbandOfferPayload = latestBroadbandOffer
      ? createBroadbandOfferPayload({
          broadbandPlanId: latestBroadbandOffer.id,
          NDCfeatureId: latestNewDevelopmentCharge
            ? latestNewDevelopmentCharge.id
            : null,
        })
      : null;

    const mobileOfferPayload = createMobileOfferPayload(mobilePlanData.id);

    mutate(
      {
        quoteSummaryRequest: {
          ...mobileOfferPayload,
          ...(broadbandOfferPayload && {
            ...broadbandOfferPayload,
          }),
        },
      },
      {
        onSuccess: () => {
          setLatestMobileOffer(mobilePlanData);
          setIsShoppingCartOpen(true);
          gtmSelectItem({
            currency: CURRENCY,
            value: planCardPrice,
            items: [
              {
                currency: CURRENCY,
                item_name: mobilePlanHeading,
                item_category: MOBILE,
                item_category2: mobilePlanSubtitle,
                item_category3: latestBroadbandOffer ? BUNDLE : NONE,
                price: planCardPrice,
                quantity: 1,
              },
            ],
          });
          gtmMobilePlanCardSelect({
            planName: mobilePlanHeading,
            totalPrice: planCardPrice,
          });
        },
      }
    );
  };

  const buttonText =
    latestMobileOffer?.id === mobilePlanData.id ? 'Added' : 'Add to Cart';
  const isButtonDisabled = latestMobileOffer?.id === mobilePlanData.id;
  return (
    <div className="bg-light-grey-200 w-full p-4 rounded-[16px] font-nunito flex flex-col gap-[16px] md:gap-[20px] md:rounded-[24px] md:p-[24px] md:pr-[21px] lg:p-8 lg:pr-[29px] lg:gap-[32px]">
      <div className="flex gap-[12px] md:gap-[16px] lg:gap-[24px]">
        <div>
          <ESimIcon className="size-[48px] md:size-[52px] lg:size-[84px]" />
        </div>
        <div className="flex flex-col text-black gap-[2.1px] md:gap-[2px] lg:gap-[4px]">
          <h3 className="text-xs font-nunito500 md:text-base">
            {mobilePlanHeading}
          </h3>
          <h3 className="font-nunito700 text-base md:text-xl lg:text-2xl">
            {mobilePlanSubtitle}
          </h3>
        </div>
      </div>
      <div className="text-black">
        <span className="text-2xl font-nunito700 md:text-3xl lg:text-4xl">
          ${planCardPrice}
        </span>
        <span className="text-xs font-nunito500 md:text-sm lg:text-lg">
          /recharge
        </span>
      </div>
      <Separator className="bg-light-grey-400" />
      <div className="flex flex-col gap-[8.1px] md:gap-[12px]">
        {/* FIXME: Remove unnecessary optional chaining. Content and features are required but for now optional. */}
        {mobilePlanData.content?.plan_features?.map((value, index) => {
          return (
            <div className="flex" key={index}>
              <CheckCircle className="size-4 shrink-0 text-warp-green-300 mr-[14px] md:size-5 md:mr-[4px] lg:size-6 lg:mr-[8px]" />
              <div className="text-xs font-nunito500 text-black md:text-sm lg:text-base [&_a]:underline">
                <ReplaceHtmlWithReact value={value.title} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-auto">
        <Separator className="bg-light-grey-400" />
        <Button
          disabled={isButtonDisabled}
          variant="primary"
          onClick={handleAddToCart}
          loading={isMutating}
          className="relative w-full text-sm max-h-[40px] md:text-base md:max-h-[52px] lg:text-lg mt-4 md:mt-5 lg:mt-8"
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default PlanCardMobile;
